import React, { useState, useEffect } from 'react';
import { FormFeedback } from 'reactstrap';
import { Chip } from "@mui/material";
import Form from 'react-bootstrap/Form'
import { SubmitActionModel, IUserSearchResult } from 'shared/models/api/models';
import FatigueAssessmentQuestions from './FatigueAssessmentQuestions'
import { ToolTipButton } from "components/menu/Labels";

import {
    FatigueAssessmentActionState,
    fatigueAssessmentActionState,
    handleActionChange,
    actionFieldInvalid,
    resetActionValidation
} from './FatigueAssessmentActionState'

import {
    UserChoice,
    SingleLineTextInput,
    MultilineTextInput,
    RadioInput,
    CheckBoxInput,
    actionOptions,
} from './FormControls';


interface SupervisorChip {
    id: number;
    name: string;
}

interface FatigueAssessmentActionProps {
    alternativeSupervisor?: boolean;
}

const FatigueAssessmentAction = (props: FatigueAssessmentActionProps) => {
    const [action, setAction] = useState({} as SubmitActionModel)
    const [affectedSupervisorsChips, setAffectedSupervisorsChips] = useState([] as SupervisorChip[]);
    const [actionTakenSelected, setActionTakenSelected] = useState(false);
    const [noActionTakenSelected, setNoActionTakenSelected] = useState(false);
    const [render, setRender] = useState({});
    const q = FatigueAssessmentQuestions;

    const handleAffectedSupervisorSelected = () => (user: IUserSearchResult) => {
        if (!user || typeof (user.id) !== 'number') {
            return;
        }

        if (affectedSupervisorsChips.findIndex(supervisor => supervisor.id === user.id) > -1) {
            removeAffectedSupervisor(user.id);
            return;
        }

        const newSupervisors = [...affectedSupervisorsChips, { id: user.id, name: user.fullname } as SupervisorChip];
        setAffectedSupervisorsChips(newSupervisors);
        const newSupervisorIds = newSupervisors.map(user => user.id);
        handleActionChange("affectedSupervisors")(newSupervisorIds);
    }

    const removeAffectedSupervisor = (userId: number) => {
        var index = affectedSupervisorsChips.findIndex(supervisor => supervisor.id === userId);
        if (index > -1) {
            var newSupervisors = [...affectedSupervisorsChips];
            newSupervisors.splice(index, 1);
            setAffectedSupervisorsChips(newSupervisors);
            const newSupervisorIds = newSupervisors.map(user => user.id);
            handleActionChange("affectedSupervisors")(newSupervisorIds);
        }
    }

    const actionTakenUpdated = (field: string) => (event: any) => {
        if (typeof (event) !== 'boolean') {
            return;
        }

        let furtherAction = false;
        let actionTaken = false;

        if (field === "noFurtherAction") {
            furtherAction = true;
        }
        else if (field === "actionTaken") {
            actionTaken = true;
        }


        //Set top level radio buttons
        handleActionChange("noFurtherAction")(furtherAction);
        handleActionChange("actionTaken")(actionTaken);


        //Always uncheck all action taken second level check boxes when top level changes
        handleActionChange("actionTakenRevisit")(false);
        handleActionChange("actionTakenBuddyUp")(false);
        handleActionChange("actionTakenRemoveFromSafetyCritical")(false);
        handleActionChange("actionTakenRemainLocal")(false);
        handleActionChange("actionTakenAltDuties")(false);
        handleActionChange("noActionTakenPlannedLeave")(false);
        handleActionChange("noActionTakenUnplannedLeave")(false);
        handleActionChange("sentHome")(false);

        //Determine if actionTaken second level check boxes should be displayed
        setActionTakenSelected(field === "actionTaken");

        //Determine if noActionTaken second level check boxes should be displayed
        setNoActionTakenSelected(field === "noFurtherAction");

    }

    const actionTakenInvalid = () => {
        return actionFieldInvalid("noFurtherAction") && actionFieldInvalid("actionTaken") && actionFieldInvalid("sentHome");
    }

    useEffect(() => {
        const sub = fatigueAssessmentActionState.subscribe((s: FatigueAssessmentActionState) => {
            setAction(s.action);
            setRender({}); //Cause new render on action change - needed for invalid fields to display as expected
        });
        resetActionValidation();

        return () => { sub.unsubscribe(); };
    }, []);

    return (
        <div>

            <div className="main-form-row row-divider">
                <div className="question">{q.controlsImplementedBy.value}<ToolTipButton text={q.controlsImplementedBy.tooltip} /></div>
                <div className="answer">
                    <UserChoice
                        supervisorFilter={true}
                        onAnswerUpdate={(user: IUserSearchResult) => handleActionChange("controlsImplementedById")(user.id)}
                        invalid={actionFieldInvalid("controlsImplementedById")} />
                </div>
            </div>

            <div className="main-form-row row-divider">
                <div className="question">{q.actionTakenGroup.value}</div>
                <div className="answer">

                    <RadioInput name={"action"} id={"noFurtherAction"} onAnswerUpdate={actionTakenUpdated} invalid={false} label={actionOptions.noFurtherAction} />
                    <div className={`pl-4${ noActionTakenSelected === true ? '' : ' d-none' }`}>
                        <CheckBoxInput id={"noActionTakenPlannedLeave"} checked={action.noActionTakenPlannedLeave}
                            onAnswerUpdate={handleActionChange} label={actionOptions.noActionTakenPlannedLeave} />
                        <CheckBoxInput id={"noActionTakenUnplannedLeave"} checked={action.noActionTakenUnplannedLeave}
                            onAnswerUpdate={handleActionChange} label={actionOptions.noActionTakenUnplannedLeave} />
                    </div>

                    <RadioInput name={"action"} id={"actionTaken"} onAnswerUpdate={actionTakenUpdated} invalid={false} label={actionOptions.actionTaken} />

                    <div className={`pl-4${ actionTakenSelected === true ? '' : ' d-none' }`}>
                        <CheckBoxInput id={"actionTakenRevisit"} checked={action.actionTakenRevisit}
                            onAnswerUpdate={handleActionChange} label={actionOptions.actionTakenRevisit} />
                        <CheckBoxInput id={"actionTakenBuddyUp"} checked={action.actionTakenBuddyUp}
                            onAnswerUpdate={handleActionChange} label={actionOptions.actionTakenBuddyUp} />
                        <CheckBoxInput id={"actionTakenRemoveFromSafetyCritical"} checked={action.actionTakenRemoveFromSafetyCritical}
                            onAnswerUpdate={handleActionChange} label={actionOptions.actionTakenRemoveFromSafetyCritical} />
                        <CheckBoxInput id={"actionTakenRemainLocal"} checked={action.actionTakenRemainLocal}
                            onAnswerUpdate={handleActionChange} label={actionOptions.actionTakenRemainLocal} />
                        <CheckBoxInput id={"actionTakenAltDuties"} checked={action.actionTakenAltDuties}
                            onAnswerUpdate={handleActionChange} label={actionOptions.actionTakenAltDuties} />
                        <CheckBoxInput id={"sentHome"} checked={action.sentHome}
                            onAnswerUpdate={handleActionChange} label={actionOptions.sentHome} />
                    </div>



                    <FormFeedback className={`${ actionTakenInvalid() ? "d-block" : "" }`}>Required</FormFeedback>

                </div>
            </div>

            <div className="main-form-row row-divider">
                <div className="question">{q.actionComment.value}</div>
                <div className="answer">
                    <MultilineTextInput
                        onAnswerUpdate={handleActionChange("comment")}
                        invalid={actionFieldInvalid("comment")} />
                </div>
            </div>

            <div className="main-form-row row-divider">
                <div className="question">{q.actionConfidential.value}<ToolTipButton text={q.actionConfidential.tooltip} /></div>
                <div className="answer">
                    <div className="answer-select">
                        <Form.Check type="checkbox" onChange={(e) => { handleActionChange("confidential")(e.target.checked) }} />
                    </div>
                </div>
            </div>

            <div className="main-form-row row-divider">
                <div className="question">{q.actionSupervisors.value}<ToolTipButton text={q.actionSupervisors.tooltip} /></div>
                <div className="answer">
                    <UserChoice
                        multichoice={true}
                        supervisorFilter={true}
                        subscribeActionField="affectedSupervisors"
                        onAnswerUpdate={handleAffectedSupervisorSelected()} />
                    {affectedSupervisorsChips.map((supervisor) => (
                        <Chip key={supervisor.id} className="m-1" label={supervisor.name} onDelete={() => removeAffectedSupervisor(supervisor.id)} />
                    ))}
                </div>
            </div>

            <div className="main-form-row">
                <div className="question">{q.actionSupervisorSignature.value}</div>
                <div className="answer">
                    <SingleLineTextInput
                        onAnswerUpdate={handleActionChange("supervisorSignature")}
                        invalid={actionFieldInvalid("supervisorSignature")}
                        placeholder="Type in your name or initials" />
                </div>
            </div>

            {props.alternativeSupervisor === true &&
                <div className="main-form-row row-divider-top">
                    <div className="question">{q.actionAlternativeSupervisorReason.value}</div>
                    <div className="answer">
                        <MultilineTextInput
                            onAnswerUpdate={handleActionChange("alternativeSupervisorReason")}
                            invalid={actionFieldInvalid("alternativeSupervisorReason")} />
                    </div>
                </div>
            }

        </div>
    );
}

export default FatigueAssessmentAction;
